import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, User, AlertCircle, Eye, EyeOff, Globe } from 'lucide-react';
import { useAuth } from '../providers/AuthProvider';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import Alert from '../components/ui/alert';

export default function RegisterPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated) navigate('/app/dashboard');
  }, [isAuthenticated, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('auth:field_required');
    if (!formData.email) {
      newErrors.email = t('auth:field_required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth:invalid_email');
    }
    if (!formData.password) {
      newErrors.password = t('auth:field_required');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth:password_requirements');
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth:field_required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth:passwords_must_match');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await register(formData.name, formData.email, formData.password);
      navigate('/onboarding');
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // ✅ EMERGENT OAUTH: REMINDER - DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/onboarding';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background bg-noise p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Globe className="w-10 h-10 text-primary" />
            <h1 className="text-3xl font-heading font-semibold tracking-tight text-text-primary">{t('common:app_name')}</h1>
          </div>
          <p className="text-text-secondary">{t('common:tagline')}</p>
        </div>

        <Card data-testid="register-card">
          <CardHeader>
            <CardTitle>{t('auth:create_your_account')}</CardTitle>
            <CardDescription>Start automating your social media today</CardDescription>
          </CardHeader>

          <CardContent>
            {errorMessage && (
              <Alert variant="error" className="mb-6" onClose={() => setErrorMessage('')} data-testid="register-error-alert">
                {errorMessage}
              </Alert>
            )}

            <Button type="button" variant="outline" className="w-full mb-6" onClick={handleGoogleSignup} data-testid="google-signup-button">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth:continue_with_google')}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-text-secondary">{t('auth:or_continue_with')}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-text-primary">{t('auth:full_name')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="name" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange} error={!!errors.name} className="pl-10" data-testid="name-input" />
                </div>
                {errors.name && <p className="text-xs text-error flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-text-primary">{t('auth:email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="email" name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} error={!!errors.email} className="pl-10" data-testid="email-input" />
                </div>
                {errors.email && <p className="text-xs text-error flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-text-primary">{t('auth:password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="password" name="password" type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.password} onChange={handleChange} error={!!errors.password} className="pl-10 pr-10" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-error flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.password}</p>}
              </div>

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-text-primary">{t('auth:confirm_password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} placeholder="••••••••" value={formData.confirmPassword} onChange={handleChange} error={!!errors.confirmPassword} className="pl-10 pr-10" data-testid="confirm-password-input" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors">
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-xs text-error flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.confirmPassword}</p>}
              </div>

              <Button type="submit" variant="primary" className="w-full" disabled={isLoading} data-testid="register-submit-button">
                {isLoading ? t('auth:creating_account') : t('auth:sign_up')}
              </Button>

              <p className="text-xs text-text-secondary text-center">
                {t('auth:by_signing_up')} <a href="#" className="text-primary hover:underline">{t('auth:terms_of_service')}</a> {t('auth:and')} <a href="#" className="text-primary hover:underline">{t('auth:privacy_policy')}</a>
              </p>
            </form>
          </CardContent>

          <CardFooter className="flex-col">
            <p className="text-sm text-text-secondary text-center">
              {t('auth:already_have_account')}{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">{t('auth:sign_in')}</Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
